import { Injectable } from "@angular/core";
import { ActivatedRoute } from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import { Action, Store } from "@ngrx/store";
import { PageNotificationService, EventBusService } from '@signature-it/ngx-generic/services';
import {EventData, EventName} from '@signature-it/ngx-generic/classes';
import { Observable, of } from 'rxjs';
import {map, withLatestFrom, switchMap, catchError, exhaustMap} from 'rxjs/operators';
import { GetTemporaryTransactionDTO, CartTotalQuantityDTO, CartTotalPriceDTO } from '@signature-it/ngx-catalogue/models';
import { CartService } from '@signature-it/ngx-catalogue/services';
import { CartActions } from "@signature-it/ngx-catalogue/store/actions";
import { CartStateModelGeneric } from "@signature-it/ngx-catalogue/store/models";
import { CodescDTO } from '@signature-it/ngx-generic/models';
import * as fromSelectors from '@signature-it/ngx-catalogue/store/selectors';

declare let _: any;

@Injectable()
export class CartEffects {

    static COUNT_ITEMS_TYPE_LINES = 'lines';
    static ATTR_COUNT_ITEMS_TYPE = 'items-count';

    constructor(
        protected notifySvc: PageNotificationService,
        protected store$: Store<any>,
        protected actions$: Actions,
        protected cartSvc: CartService,
        protected route: ActivatedRoute,
        protected eventBusSvc: EventBusService) {
    }

    addToCart$=  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.ADD_TO_CART),
            map((action) => action['payload']),
            withLatestFrom(this.store$.select(fromSelectors.getCartState)),
            switchMap(([payload, cartState]) => {
                return this.cartSvc.add(payload).pipe(
                    map(data => {
                        if (cartState.updateAllQuantities) this.store$.dispatch(CartActions.getCart());
                        return CartActions.addToCartSuccess({products: payload, total: this.getUpdatedTotal(data, cartState)}, {'afterRemoveAll': cartState.addToCartAfterRemove});
                    }),
                    catchError((error) => of(CartActions.addToCartFailure(error)))
                );
            }))
        );

    getCart$ =  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.GET_CART),
        switchMap(() => {
            const params = this.route.snapshot.queryParams;
            return this.cartSvc.get(params).pipe(
                map((data: GetTemporaryTransactionDTO) => CartActions.gotCartSuccess(data)),
                catchError((error) => of(CartActions.gotCartFailure(error)))
            );
        }))
    );

    getTotalQuantity$=  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.GET_ITEMS_QUANTITY),
            switchMap(() => {
                return this.cartSvc.getTotalQuantity().pipe(
                    map((data: CartTotalQuantityDTO) => CartActions.gotItemsQuantitySuccess(data)),
                    catchError((error) => of(CartActions.gotItemsQuantityFailure(error)))
                );
            }))
        );

    removeItem$ =  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.REMOVE_ITEM),
            map((action) => action['payload']),
            withLatestFrom(this.store$.select(fromSelectors.getCartState)),
            switchMap(([payload, cartState]) => {
                return this.cartSvc.clearItem(payload).pipe(
                    map((data) => CartActions.removeItemSuccess(this.removeItemSuccess(data, cartState))),
                    catchError((error) => of(CartActions.removeItemFailure(error)))
                );
            }))
        );

    removeAllItem$=  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.REMOVE_ALL_ITEMS),
            map((action) => action['payload']),
            withLatestFrom(this.store$.select(fromSelectors.getCartState)),
            switchMap(([payload, cartState]) => {
                return this.cartSvc.clearAll().pipe(
                    map((data) => CartActions.removeAllItemsSuccess(this.removeAllItemsSuccess(cartState))),
                    catchError((error) => of(CartActions.removeAllItemsFailure(error)))
                );
            }))
        );

    getTotalPrice$=  createEffect(() =>
        this.actions$
        .pipe(ofType(CartActions.GET_TOTAL_PRICE),
            map((action) => action),
            switchMap((action) => {
                return this.cartSvc.getTotalPrice(action['removeDelivery'],  action['formCheckout']).pipe(
                    map((data ) => CartActions.gotTotalPriceSuccess(CartTotalPriceDTO.create(data))),
                    catchError((error) => of(CartActions.gotTotalPriceFailure(error)))
                );
            }))
        );

    checkout$=  createEffect(() => this.actions$
        .pipe(ofType(CartActions.CHECKOUT),
            map((action) => action['payload']),
            withLatestFrom(this.store$.select(fromSelectors.getCartState)),
            switchMap(([payload, cartState]) => {
                return this.cartSvc.checkout(payload).pipe(
                    map((data) => CartActions.checkoutSuccess(this.checkoutSuccess(data, payload, cartState))),
                    catchError((error) => of(CartActions.checkoutFailure(error)))
                );
            }))
        );

    getShippingMethods$=  createEffect(() => this.actions$
        .pipe(ofType(CartActions.GET_SHIPPINIG_METHOD),
            switchMap(() => {
                return this.cartSvc.getShippingMethods().pipe(
                    map((data: CodescDTO[]) => CartActions.gotShippingMethodsSuccess(data)),
                    catchError((error) => of(CartActions.gotShippingMethodsFailure(error)))
                );
            }))
        );

    protected removeItemSuccess(response, cartState: CartStateModelGeneric) {

        let cart = cartState.cart;
        const orderId = _.keys(response)[0];
        // find the removed item
        const item = _.find(cart.Transaction['TransactionItems'], (item) =>  item['OrderID'] === parseInt(orderId));
        cart.Transaction['TransactionItems'] = _.filter(cart.Transaction['TransactionItems'], (p) =>  p['ParentLineId'] !== orderId && p['OrderID'] !== parseInt(orderId));
        if (cart.Transaction['TransactionItems'].length) {
            this.getTotalPrice();
        }
        return {
            cart: GetTemporaryTransactionDTO.create(cart),
            quantity: cartState.itemsQuantity.total_quantity - _.values(response)[0],
            cartProductIds: cartState.cartProductIds.splice(item['ProductID'], 1)
        };
    }
    protected removeAllItemsSuccess(cartState: CartStateModelGeneric) {
        let cart = cartState.cart;
        cart.Transaction['TransactionItems'] = [];
        return {
            cart: GetTemporaryTransactionDTO.create(cart)
        };
    }

    protected removeAllItemsAfterCheckout() {
        const cart = new GetTemporaryTransactionDTO({});
        return {
            cart
        };
    }

    protected getTotalPrice() {
        const action = CartActions.getTotalPrice();
        this.store$.dispatch(action);
    }

    protected checkoutSuccess(result, payload, cartState) {
        if(payload.isCheckoutSuccessEvent) {
            this.eventBusSvc.emit(new EventData(EventName.AFTER_CHECKOUT_SUCCESS, result));
            return cartState.cart
        } else {
            if(result) {
                window.location.href = result;
            } else {
                this.notifySvc.set("Cant redirect to " + payload.serviceType).show();
            }
            return this.removeAllItemsAfterCheckout();
        }

    }

    protected getUpdatedTotal(data, cartState: CartStateModelGeneric) {
        let total = cartState.itemsQuantity.total_quantity;
        const productsIds = _.keys(data['products']).map(id => parseInt(id));
        const existIdsInCart = cartState.cartProductIds;
        if (data[CartEffects.ATTR_COUNT_ITEMS_TYPE] === CartEffects.COUNT_ITEMS_TYPE_LINES) {
            productsIds.forEach(id => {
                if (existIdsInCart.indexOf(id) === -1) {
                    total ++;
                }
            });
        } else  {
            total += _.reduce(_.values(data['products']), function(memo, num) { return memo + num; }, 0);
        }
        return total;
    }

}
